import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function AuthPage() {
  const { login, register, error } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok =
      mode === "login"
        ? await login(email, password)
        : await register(email, password, name || email.split("@")[0]);
    setBusy(false);
    if (ok) toast.success(mode === "login" ? "Welkom terug" : "Account aangemaakt");
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#0B1120]">
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, rgba(37,99,235,0.35) 0%, rgba(11,17,32,1) 60%)",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Logo size={30} />
          <div>
            <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight text-slate-50 leading-tight">
              Je hele leven,<br />prachtig georganiseerd.
            </h1>
            <p className="mt-6 text-slate-400 max-w-md leading-relaxed">
              Nova is jouw persoonlijke commandocentrum voor de dag, week en maand —
              van school en werk tot sport, boodschappen, rust en belangrijke momenten.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {[
                { label: "Vandaag", key: "today" },
                { label: "Week", key: "week" },
                { label: "Maand", key: "month" },
              ].map((t) => (
                <div
                  key={t.key}
                  className="rounded-lg border border-slate-800 bg-[#121C2F]/60 px-3 py-4 text-center"
                >
                  <div className="text-xs uppercase tracking-widest text-slate-500">Weergave</div>
                  <div className="mt-1 text-slate-100 font-medium">{t.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-500">© Nova · Gebouwd voor focus</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8"><Logo /></div>
          <h2 className="text-3xl font-semibold tracking-tight text-slate-50">
            {mode === "login" ? "Welkom terug" : "Maak je account aan"}
          </h2>
          <p className="text-slate-400 mt-2 text-sm">
            {mode === "login"
              ? "Meld je aan om verder te plannen."
              : "Begin met een schone lei. Voor altijd gratis."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-slate-300">Naam</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 bg-[#121C2F] border-slate-800 text-slate-100"
                  placeholder="Je naam"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-slate-300">E-mail</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-[#121C2F] border-slate-800 text-slate-100"
                placeholder="jij@voorbeeld.nl"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1.5 bg-[#121C2F] border-slate-800 text-slate-100"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium"
            >
              {busy ? "Even geduld..." : mode === "login" ? "Aanmelden" : "Account aanmaken"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-400">
            {mode === "login" ? (
              <>Nog geen account?{" "}
                <button
                  data-testid="auth-toggle-register"
                  onClick={() => setMode("register")}
                  className="text-blue-400 hover:text-blue-300 font-medium"
                >Maak er een aan</button>
              </>
            ) : (
              <>Heb je al een account?{" "}
                <button
                  data-testid="auth-toggle-login"
                  onClick={() => setMode("login")}
                  className="text-blue-400 hover:text-blue-300 font-medium"
                >Meld je aan</button>
              </>
            )}
          </div>

          <div className="mt-10 text-xs text-slate-600">
            Demo: <span className="text-slate-400">admin@nova.app / admin123</span>
          </div>
        </div>
      </div>
    </div>
  );
}
